import React, { useState, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, RefreshControl, TouchableOpacity, TextInput } from 'react-native';
import { useLocalSearchParams, Stack, useRouter, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useOwnerScope } from '../../../../src/context/OwnerScopeContext';
import Card from '../../../../src/components/Card';
import Button from '../../../../src/components/Button';
import UpsellPolicyPicker, { type UpsellPolicy, type UpsellCatalogItem } from '../../../../src/components/upsells/UpsellPolicyPicker';
import Input from '../../../../src/components/Input';
import { useToast } from '../../../../src/components/Toast';
import InlineConfirm from '../../../../src/components/InlineConfirm';
import ContentContainer from '../../../../src/components/ContentContainer';
import { useTheme } from '../../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../../src/utils/theme';
import { formatDateTime } from '../../../../src/utils/timezone';
import { titleCase } from '../../../../src/utils/format';
import { bundleItemsSummary } from '../../../../src/utils/bundleItems';

interface Location {
  id: string;
  name: string;
}

interface WashTier {
  id: string;
  name: string;
  priceInCents: number;
  isActive: boolean;
}

interface BundlePlanItem {
  kind: 'wash' | 'vac_minutes' | 'vending_credit';
  tierId: string | null;
  tierName: string | null;
  quantity: number | null;
  minutes: number | null;
  creditCents: number | null;
  // vac_minutes only: must be used in a single session (stop forfeits rest).
  allAtOnce?: boolean;
}

interface BundlePlan {
  upsellPolicy?: Record<string, 'none' | 'purchasable' | 'included'>;
  id: string;
  name: string;
  description: string | null;
  tierName: string;
  washCount: number;
  items?: BundlePlanItem[];
  bundlePrice: number;
  locationScope: string;
  locationIds: string[] | null;
  locationNames: string[];
  expirationDays: number | null;
  expirationMinutes?: number | null;
  isActive: boolean;
  purchaserCount: number;
}

interface Purchaser {
  id: string;
  userId: string;
  userName: string;
  accountNumber: string;
  status: string;
  washesTotal: number;
  washesUsed: number;
  washesRemaining: number;
  amountPaid: number;
  expiresAt: string | null;
  purchasedAt: string;
}

/** BundleDetailScreen - view/edit wash bundle and manage purchasers. */
export default function BundleDetailScreen({ entityId }: { entityId?: string } = {}) {
  const { colors } = useTheme();
  const styles = useStyles(colors);

  const params = useLocalSearchParams<{ id: string }>();
  const id = entityId ?? params.id;
  const router = useRouter();
  const [plan, setPlan] = useState<BundlePlan | null>(null);
  const [purchasers, setPurchasers] = useState<Purchaser[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [refunding, setRefunding] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [refundConfirmPurchId, setRefundConfirmPurchId] = useState<string | null>(null);
  const toast = useToast();
  const scope = useOwnerScope();

  // Editable fields
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editExpMode, setEditExpMode] = useState<'never' | 'days' | 'visit'>('never');
  const [editExpDays, setEditExpDays] = useState('');
  const [editExpMinutes, setEditExpMinutes] = useState('120');
  const [editUpsellPolicy, setEditUpsellPolicy] = useState<UpsellPolicy>({}); // 1.4 R05
  const [upsellCatalog, setUpsellCatalog] = useState<UpsellCatalogItem[]>([]);
  const [editLocationScope, setEditLocationScope] = useState<'selected' | 'all'>('all');
  const [editLocationIds, setEditLocationIds] = useState<string[]>([]);
  // Editable even though items are otherwise locked: it changes session
  // behavior for FUTURE purchases only (sold packs keep their snapshot).
  const [editVacAllAtOnce, setEditVacAllAtOnce] = useState(false);
  const [locationsList, setLocationsList] = useState<Location[]>([]);
  // À-la-carte valuation inputs: wash tier catalog prices + the HIGHEST active
  // vacuum master rate ($/min) across locations. vacRate null = no vacuums.
  const [washTiers, setWashTiers] = useState<WashTier[]>([]);
  const [vacRate, setVacRate] = useState<number | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plans, purchs, locs, tiersCatalog] = await Promise.all([
        scope.api.get<BundlePlan[]>('/api/bundle-plans'),
        scope.api.get<Purchaser[]>(`/api/bundle-plans/${id}/purchasers`),
        scope.api.get<Location[]>('/api/locations'),
        scope.api.get<WashTier[]>('/api/wash-tiers').catch(() => [] as WashTier[]),
      ]);
      setLocationsList(locs);
      setWashTiers(tiersCatalog);
      // Best-effort vac rate — the value row simply shows the unpriced hint
      // when this fails or the owner has no active vacuums.
      Promise.all(locs.map(l =>
        scope.api.get<{ type: string; pricePerMinute: number | null; isActive: boolean }[]>(`/api/locations/${l.id}/bays`)
          .catch(() => [] as { type: string; pricePerMinute: number | null; isActive: boolean }[])))
        .then(baysPerLoc => {
          const rates = baysPerLoc.flat()
            .filter(b => b.type === 'vacuum' && b.isActive && b.pricePerMinute != null && b.pricePerMinute > 0)
            .map(b => b.pricePerMinute as number);
          setVacRate(rates.length > 0 ? Math.max(...rates) : null);
        });
      const thisPlan = plans.find(p => p.id === id);
      if (thisPlan) {
        setPlan(thisPlan);
        setEditName(thisPlan.name);
        setEditDescription(thisPlan.description || '');
        setEditPrice((thisPlan.bundlePrice / 100).toFixed(2));
        setEditExpMode(thisPlan.expirationMinutes ? 'visit' : thisPlan.expirationDays ? 'days' : 'never');
        setEditExpDays(thisPlan.expirationDays ? String(thisPlan.expirationDays) : '');
        setEditExpMinutes(thisPlan.expirationMinutes ? String(thisPlan.expirationMinutes) : '120');
        setEditLocationScope(thisPlan.locationScope as 'selected' | 'all');
        setEditLocationIds(thisPlan.locationIds || []);
        setEditVacAllAtOnce(!!thisPlan.items?.some(i => i.kind === 'vac_minutes' && i.allAtOnce));
      }
      setPurchasers(purchs);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  useFocusEffect(useCallback(() => { fetchData(); }, []));

  const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  // À-la-carte value of the bundle's contents: wash items at catalog tier
  // price, vac minutes at the highest active vacuum master rate, vending
  // credit at face value. unpricedVac = vac minutes present but no vacuum yet.
  const alaCarte = useMemo(() => {
    if (!plan) return null;
    let total = 0;
    let unpricedVac = false;
    const tierPrice = (tierId: string | null, tierName: string | null): number => {
      const t = (tierId ? washTiers.find(w => w.id === tierId) : undefined)
        ?? (tierName ? washTiers.find(w => w.name === tierName) : undefined);
      return t?.priceInCents ?? 0;
    };
    if (plan.items?.length) {
      for (const it of plan.items) {
        if (it.kind === 'wash') total += tierPrice(it.tierId, it.tierName) * (it.quantity ?? 0);
        else if (it.kind === 'vac_minutes') {
          if (vacRate != null) total += Math.round((it.minutes ?? 0) * vacRate * 100);
          else if ((it.minutes ?? 0) > 0) unpricedVac = true;
        } else if (it.kind === 'vending_credit') total += it.creditCents ?? 0;
      }
    } else {
      total = tierPrice(null, plan.tierName) * plan.washCount;
    }
    return { total, unpricedVac };
  }, [plan, washTiers, vacRate]);

  const filteredPurchasers = useMemo(() => {
    if (!searchQuery.trim()) return purchasers;
    const q = searchQuery.toLowerCase().trim();
    return purchasers.filter(p =>
      p.userName.toLowerCase().includes(q) ||
      p.accountNumber.toLowerCase().includes(q) ||
      p.status.toLowerCase().includes(q)
    );
  }, [purchasers, searchQuery]);

  const startEditing = () => {
    if (!plan) return;
    setEditName(plan.name);
    setEditDescription(plan.description || '');
    setEditPrice((plan.bundlePrice / 100).toFixed(2));
    setEditExpMode(plan.expirationMinutes ? 'visit' : plan.expirationDays ? 'days' : 'never');
    setEditExpDays(plan.expirationDays ? String(plan.expirationDays) : '');
    setEditExpMinutes(plan.expirationMinutes ? String(plan.expirationMinutes) : '120');
    setEditLocationScope(plan.locationScope as 'selected' | 'all');
    setEditLocationIds(plan.locationIds || []);
    setEditVacAllAtOnce(!!plan.items?.some(i => i.kind === 'vac_minutes' && i.allAtOnce));
    setEditUpsellPolicy({ ...(plan.upsellPolicy ?? {}) });
    scope.api.get<UpsellCatalogItem[]>('/api/upsells').then(list => setUpsellCatalog(list.filter(u => u.isActive !== false))).catch(() => setUpsellCatalog([]));
    setEditing(true);
  };

  const saveChanges = async () => {
    if (!plan) return;
    if (!editName.trim()) {
      toast.show({ type: 'error', title: 'Error', message: 'Bundle name is required' });
      return;
    }
    const priceCents = Math.round(parseFloat(editPrice) * 100);
    if (!priceCents || priceCents <= 0) {
      toast.show({ type: 'error', title: 'Error', message: 'Enter a valid price' });
      return;
    }

    if (editLocationScope === 'selected' && editLocationIds.length === 0) {
      toast.show({ type: 'error', title: 'Error', message: 'Select at least one location' });
      return;
    }

    const expDaysRaw = editExpDays.trim() ? parseInt(editExpDays) : null;
    const expDays = editExpMode === 'days' && expDaysRaw && expDaysRaw > 0 ? expDaysRaw : null;
    if (editExpMode === 'days' && !expDays) {
      toast.show({ type: 'error', title: 'Error', message: 'Enter valid expiration days' });
      return;
    }
    let expMinutes: number | null = null;
    if (editExpMode === 'visit') {
      const mins = parseInt(editExpMinutes);
      if (!mins || mins < 15 || mins > 10080) {
        toast.show({ type: 'error', title: 'Error', message: 'Same-visit window must be 15–10080 minutes' });
        return;
      }
      expMinutes = mins;
    }

    // Items are otherwise locked, but the vac "one session" flag is editable
    // (affects future purchases only — sold packs keep their snapshot). Resend
    // the item list verbatim with the flag applied to vac lines.
    // Legacy wash items without a tierId can't round-trip the items schema —
    // skip the resend for those plans (the flag simply stays uneditable).
    const hasVacItem = !!plan.items?.some(i => i.kind === 'vac_minutes')
      && !!plan.items?.every(i => i.kind !== 'wash' || !!i.tierId);
    const updatedItems = hasVacItem && plan.items
      ? plan.items.map(i =>
        i.kind === 'wash'
          ? { kind: 'wash' as const, tierId: i.tierId!, quantity: i.quantity ?? 1 }
          : i.kind === 'vac_minutes'
            ? { kind: 'vac_minutes' as const, minutes: i.minutes ?? 1, allAtOnce: editVacAllAtOnce }
            : { kind: 'vending_credit' as const, creditCents: i.creditCents ?? 25 })
      : undefined;

    setSaving(true);
    try {
      await scope.api.api(`/api/bundle-plans/${id}`, {
        method: 'PUT',
        body: {
          name: editName.trim(),
          description: editDescription.trim() || null,
          bundlePrice: priceCents,
          expirationDays: expDays,
          expirationMinutes: expMinutes,
          locationScope: editLocationScope,
          locationIds: editLocationScope === 'all' ? null : editLocationIds,
          upsellPolicy: editUpsellPolicy,
          ...(updatedItems ? { items: updatedItems } : {}),
        },
      });
      const locNames = editLocationScope === 'selected'
        ? editLocationIds.map(lid => locationsList.find(l => l.id === lid)?.name).filter(Boolean) as string[]
        : [];
      setPlan({
        ...plan,
        name: editName.trim(),
        description: editDescription.trim() || null,
        bundlePrice: priceCents,
        expirationDays: expDays,
        expirationMinutes: expMinutes,
        locationScope: editLocationScope,
        locationIds: editLocationScope === 'all' ? null : editLocationIds,
        locationNames: locNames,
        upsellPolicy: editUpsellPolicy,
        ...(hasVacItem && plan.items ? {
          items: plan.items.map(i => i.kind === 'vac_minutes' ? { ...i, allAtOnce: editVacAllAtOnce } : i),
        } : {}),
      });
      setEditing(false);
      toast.show({ type: 'success', title: 'Bundle Updated' });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    if (!plan) return;
    setSaving(true);
    try {
      await scope.api.api(`/api/bundle-plans/${id}`, {
        method: 'PUT',
        body: { isActive: !plan.isActive },
      });
      setPlan({ ...plan, isActive: !plan.isActive });
      toast.show({ type: 'success', title: plan.isActive ? 'Bundle Deactivated' : 'Bundle Activated' });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await scope.api.api(`/api/bundle-plans/${id}`, { method: 'DELETE' });
      toast.show({ type: 'success', title: 'Bundle Deleted' });
      router.back();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const getDeleteMessage = () => {
    const activeCount = purchasers.filter(p => p.status === 'active').length;
    return activeCount > 0
      ? `This bundle has ${activeCount} active purchaser${activeCount > 1 ? 's' : ''}. Deleting will deactivate it so no one else can buy it, but existing bundles remain usable. Delete this bundle plan?`
      : 'Delete this bundle plan? This cannot be undone.';
  };

  const handleRefund = async (p: Purchaser) => {
    setRefunding(p.id);
    try {
      await scope.api.post(`/api/bundles/${p.id}/refund`, {});
      toast.show({ type: 'success', title: 'Refunded', message: `${titleCase(p.userName)}'s bundle has been refunded.` });
      setRefundConfirmPurchId(null);
      fetchData();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Refund Failed', message: err.message });
    } finally {
      setRefunding(null);
    }
  };

  const exportCsv = async () => {
    if (!plan || purchasers.length === 0) return;
    const headers = ['Account #', 'Name', 'Status', 'Used', 'Remaining', 'Total', 'Amount Paid', 'Expires', 'Purchased'];
    const rows = purchasers.map(p => [
      p.accountNumber,
      p.userName,
      p.status,
      String(p.washesUsed),
      String(p.washesRemaining),
      String(p.washesTotal),
      fmt(p.amountPaid),
      p.expiresAt ? new Date(p.expiresAt).toLocaleDateString() : 'Never',
      new Date(p.purchasedAt).toLocaleDateString(),
    ]);

    const csvContent = [headers, ...rows].map(row =>
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    try {
      const { exportCsvFromString } = await import('../../../../src/utils/fileExport');
      await exportCsvFromString(csvContent, `${plan.name.replace(/[^a-zA-Z0-9]/g, '_')}_purchasers.csv`);
      toast.show({ type: 'success', title: 'CSV Downloaded' });
    } catch (err) {
      console.error('CSV export failed:', err);
      toast.show({ type: 'error', title: 'Export Failed' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return colors.success;
      case 'exhausted': return colors.textSecondary;
      case 'expired': return colors.warning;
      case 'refunded': return colors.danger;
      default: return colors.textSecondary;
    }
  };

  if (loading || !plan) {
    return (
      <>
        <Stack.Screen options={{ title: 'Bundle Details' }} />
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading...</Text>
        </View>
      </>
    );
  }

  return (
    <>
      <Stack.Screen options={{ title: plan.name }} />
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.content}
        refreshControl={<RefreshControl refreshing={loading} onRefresh={fetchData} />}
      >
        <ContentContainer>
        {/* Plan Info */}
        {editing ? (
          <Card style={styles.infoCard}>
            <Text style={styles.editSectionTitle}>Edit Bundle</Text>
            {/* Items are deliberately read-only: already-sold packs snapshot
                them, and changing them would make the same product mean
                different contents for different buyers. Create a new bundle. */}
            <View style={{ marginBottom: spacing.md }}>
              <Text style={styles.infoLabel}>{plan.items?.length ? 'Items' : 'Wash Tier'}</Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: 2 }}>
                <Ionicons name="lock-closed" size={14} color={colors.textLight} />
                <Text style={styles.infoValue}>
                  {plan.items?.length
                    ? bundleItemsSummary(plan.items)
                    : (/^[1-4]$/.test(plan.tierName) ? `Option ${plan.tierName}` : plan.tierName)}
                </Text>
              </View>
              <Text style={{ fontSize: fontSize.xs, color: colors.textLight, marginTop: 2 }}>
                Locked — sold packs are tied to these items. Create a new bundle to offer different contents.
              </Text>
              {/* Session-behavior flag stays editable — it applies to future
                  purchases only (sold packs snapshot it). */}
              {plan.items?.some(i => i.kind === 'vac_minutes') && (
                <>
                  <TouchableOpacity
                    style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm }}
                    onPress={() => setEditVacAllAtOnce(!editVacAllAtOnce)}
                  >
                    <Ionicons name={editVacAllAtOnce ? 'checkbox' : 'square-outline'} size={22} color={editVacAllAtOnce ? colors.primary : colors.textLight} />
                    <Text style={{ flex: 1, fontSize: fontSize.sm, color: colors.text }}>
                      Vacuum minutes must be used in one session
                    </Text>
                  </TouchableOpacity>
                  <Text style={{ fontSize: fontSize.xs, color: colors.textLight }}>
                    Stopping early forfeits the rest. Applies to future purchases only.
                  </Text>
                </>
              )}
            </View>
            <Input label="Bundle Name" value={editName} onChangeText={setEditName} />
            <Input label="Description" value={editDescription} onChangeText={setEditDescription} placeholder="Optional" multiline maxLength={500} />
            <UpsellPolicyPicker catalog={upsellCatalog} value={editUpsellPolicy} onChange={setEditUpsellPolicy} title="Add-ons with bundle washes" hint="Included = free with every bundle wash. Purchasable = offered at the regular price. Not offered = hidden." />
            <Input label="Bundle Price ($)" value={editPrice} onChangeText={setEditPrice} keyboardType="decimal-pad" />
            <Text style={[styles.infoLabel, { marginBottom: spacing.xs }]}>Expiration</Text>
            <View style={{ flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.sm, flexWrap: 'wrap' }}>
              {([['never', 'Never'], ['days', 'Days'], ['visit', 'Same Visit']] as const).map(([mode, label]) => (
                <TouchableOpacity
                  key={mode}
                  onPress={() => setEditExpMode(mode)}
                  style={[styles.scopeChip, editExpMode === mode && { backgroundColor: colors.primary, borderColor: colors.primary }]}
                >
                  <Text style={[styles.scopeChipText, editExpMode === mode && { color: '#fff' }]}>{label}</Text>
                </TouchableOpacity>
              ))}
            </View>
            {editExpMode === 'days' && (
              <Input label="Days Until Expiry" value={editExpDays} onChangeText={setEditExpDays} keyboardType="number-pad" placeholder="45" />
            )}
            {editExpMode === 'visit' && (
              <Input label="Same-Visit Window (minutes)" value={editExpMinutes} onChangeText={(v: string) => setEditExpMinutes(v.replace(/[^0-9]/g, ''))} keyboardType="number-pad" placeholder="120" />
            )}
            <Text style={[styles.infoLabel, { marginTop: spacing.md, marginBottom: spacing.xs }]}>Location Scope</Text>
            <View style={{ flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.sm }}>
              {(['all', 'selected'] as const).map(scope => (
                <TouchableOpacity
                  key={scope}
                  onPress={() => { setEditLocationScope(scope); if (scope === 'all') setEditLocationIds([]); }}
                  style={[styles.scopeChip, editLocationScope === scope && { backgroundColor: colors.primary, borderColor: colors.primary }]}
                >
                  <Text style={[styles.scopeChipText, editLocationScope === scope && { color: '#fff' }]}>
                    {scope === 'all' ? 'All Locations' : 'Selected'}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            {editLocationScope === 'selected' && (
              <View style={{ gap: spacing.xs }}>
                {locationsList.map(loc => (
                  <TouchableOpacity
                    key={loc.id}
                    onPress={() => setEditLocationIds(prev => prev.includes(loc.id) ? prev.filter(x => x !== loc.id) : [...prev, loc.id])}
                    style={[styles.locOption, editLocationIds.includes(loc.id) && { borderColor: colors.primary, backgroundColor: colors.primary + '10' }]}
                  >
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
                      <Ionicons name={editLocationIds.includes(loc.id) ? 'checkbox' : 'square-outline'} size={20} color={editLocationIds.includes(loc.id) ? colors.primary : colors.textLight} />
                      <Text style={[styles.locOptionText, editLocationIds.includes(loc.id) && { color: colors.primary, fontWeight: fontWeight.semibold }]}>{loc.name}</Text>
                    </View>
                  </TouchableOpacity>
                ))}
              </View>
            )}
            <View style={styles.editButtons}>
              <Button title="Cancel" onPress={() => setEditing(false)} variant="outline" style={{ flex: 1 }} />
              <Button title="Save" onPress={saveChanges} loading={saving} style={{ flex: 1 }} />
            </View>
          </Card>
        ) : (
          <Card style={styles.infoCard}>
            {plan.items?.length ? (
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Items</Text>
                <Text style={[styles.infoValue, { flex: 1, textAlign: 'right' }]}>{bundleItemsSummary(plan.items)}</Text>
              </View>
            ) : (
              <>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Tier</Text>
                  <Text style={styles.infoValue}>{/^[1-4]$/.test(plan.tierName) ? `Option ${plan.tierName}` : plan.tierName}</Text>
                </View>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Washes</Text>
                  <Text style={styles.infoValue}>{plan.washCount} per bundle</Text>
                </View>
              </>
            )}
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Price</Text>
              <Text style={styles.infoValue}>{fmt(plan.bundlePrice)}</Text>
            </View>
            {alaCarte && (alaCarte.total > 0 || alaCarte.unpricedVac) && (
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>À-la-carte value</Text>
                <Text style={[styles.infoValue, { flex: 1, textAlign: 'right' }]}>
                  {alaCarte.total > 0 ? fmt(alaCarte.total) : '—'}
                  {alaCarte.total > plan.bundlePrice
                    ? ` (customers save ${Math.round(((alaCarte.total - plan.bundlePrice) / alaCarte.total) * 100)}%)`
                    : ''}
                  {alaCarte.unpricedVac ? ' + vac min (add a vacuum to price this)' : ''}
                </Text>
              </View>
            )}
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Scope</Text>
              <Text style={styles.infoValue}>
                {plan.locationScope === 'all' ? 'All locations' : (plan.locationNames.length > 0 ? plan.locationNames.join(', ') : 'Selected locations')}
              </Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Expiration</Text>
              <Text style={styles.infoValue}>
                {plan.expirationMinutes
                  ? `Same visit (${plan.expirationMinutes} min)`
                  : plan.expirationDays ? `${plan.expirationDays} days` : 'Never'}
              </Text>
            </View>
            {plan.description && (
              <View style={[styles.infoRow, { borderBottomWidth: 0 }]}>
                <Text style={styles.infoLabel}>Description</Text>
                <Text style={[styles.infoValue, { flex: 1, textAlign: 'right' }]}>{plan.description}</Text>
              </View>
            )}
            <Button
              title="Edit Bundle"
              onPress={startEditing}
              variant="outline"
              size="sm"
              icon={<Ionicons name="pencil" size={16} color={colors.primary} />}
              style={{ marginTop: spacing.md }}
            />
          </Card>
        )}

        {/* Action buttons */}
        <View style={styles.actionRow}>
          <Button
            title={plan.isActive ? 'Deactivate' : 'Activate'}
            onPress={toggleActive}
            loading={saving}
            variant={plan.isActive ? 'outline' : 'primary'}
            style={{ flex: 1 }}
          />
          <Button
            title="Delete"
            onPress={() => setShowDeleteConfirm(true)}
            loading={deleting}
            variant="outline"
            style={{ flex: 1, borderColor: colors.danger }}
            textStyle={{ color: colors.danger }}
            icon={<Ionicons name="trash" size={16} color={colors.danger} />}
          />
        </View>
        <InlineConfirm
          message={getDeleteMessage()}
          visible={showDeleteConfirm}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          confirmLabel="Delete Bundle"
          loading={deleting}
          variant="danger"
        />

        {/* Purchasers header */}
        <View style={styles.subsHeaderRow}>
          <Text style={styles.sectionTitle}>
            Purchasers ({filteredPurchasers.length}{searchQuery ? ` of ${purchasers.length}` : ''})
          </Text>
          {purchasers.length > 0 && (
            <TouchableOpacity onPress={exportCsv} style={styles.exportBtn}>
              <Ionicons name="download-outline" size={16} color={colors.primary} />
              <Text style={styles.exportText}>Export CSV</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Search bar */}
        {purchasers.length > 0 && (
          <View style={styles.searchRow}>
            <Ionicons name="search" size={18} color={colors.textLight} />
            <TextInput
              style={styles.searchInput}
              placeholder="Search by name, account #, or status..."
              placeholderTextColor={colors.textLight}
              value={searchQuery}
              onChangeText={setSearchQuery}
              maxLength={100}
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity onPress={() => setSearchQuery('')}>
                <Ionicons name="close-circle" size={18} color={colors.textLight} />
              </TouchableOpacity>
            )}
          </View>
        )}

        {purchasers.length === 0 && (
          <Card style={{ alignItems: 'center', padding: spacing.lg }}>
            <Text style={{ color: colors.textLight }}>No purchasers yet</Text>
          </Card>
        )}

        {purchasers.length > 0 && filteredPurchasers.length === 0 && (
          <Card style={{ alignItems: 'center', padding: spacing.lg }}>
            <Text style={{ color: colors.textLight }}>No purchasers match "{searchQuery}"</Text>
          </Card>
        )}

        {filteredPurchasers.map(p => (
          <Card key={p.id} style={styles.subCard}>
            <View style={styles.subHeader}>
              <View style={{ flex: 1 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
                  <Text style={styles.subName}>{titleCase(p.userName)}</Text>
                  <Text style={styles.acctBadge}>#{p.accountNumber}</Text>
                </View>
              </View>
              <View style={[styles.statusBadge, { backgroundColor: getStatusColor(p.status) + '20' }]}>
                <Text style={[styles.statusText, { color: getStatusColor(p.status) }]}>{p.status}</Text>
              </View>
            </View>

            {/* Usage bar */}
            <View style={styles.usageBarContainer}>
              <View style={[styles.usageBarFill, { width: `${(p.washesUsed / p.washesTotal) * 100}%` }]} />
            </View>

            <View style={styles.subStats}>
              <Text style={styles.subStat}>Used: {p.washesUsed}/{p.washesTotal}</Text>
              <Text style={styles.subStat}>Remaining: {p.washesRemaining}</Text>
              <Text style={styles.subStat}>Paid: {fmt(p.amountPaid)}</Text>
              {p.expiresAt && (
                <Text style={styles.subStat}>Expires: {formatDateTime(p.expiresAt)}</Text>
              )}
              <Text style={styles.subStat}>Purchased: {formatDateTime(p.purchasedAt)}</Text>
            </View>

            {p.status === 'active' && (
              <>
                <Button
                  title={refunding === p.id ? 'Refunding...' : 'Refund'}
                  onPress={() => setRefundConfirmPurchId(p.id)}
                  loading={refunding === p.id}
                  variant="outline"
                  size="sm"
                  style={{ marginTop: spacing.sm, borderColor: colors.danger }}
                  textStyle={{ color: colors.danger }}
                  icon={<Ionicons name="card-outline" size={14} color={colors.danger} />}
                />
                <InlineConfirm
                  message={`Refund ${titleCase(p.userName)}'s bundle (${fmt(p.amountPaid)})? ${p.washesUsed > 0 ? `They've used ${p.washesUsed} of ${p.washesTotal} washes.` : ''} This cannot be undone.`}
                  visible={refundConfirmPurchId === p.id}
                  onConfirm={() => handleRefund(p)}
                  onCancel={() => setRefundConfirmPurchId(null)}
                  confirmLabel="Refund"
                  loading={refunding === p.id}
                  variant="warning"
                />
              </>
            )}
          </Card>
        ))}

        <View style={{ height: spacing.xxl }} />
        </ContentContainer>
      </ScrollView>
    </>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { fontSize: fontSize.md, color: c.textSecondary },

  infoCard: { marginBottom: spacing.md },
  infoRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  infoLabel: { fontSize: fontSize.sm, color: c.textSecondary },
  infoValue: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },

  editSectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.md },
  editButtons: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.md },
  scopeChip: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.borderLight },
  scopeChipText: { fontSize: fontSize.sm, color: c.text },
  locOption: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.borderLight },
  locOptionText: { fontSize: fontSize.sm, color: c.text },

  actionRow: { flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.md },

  subsHeaderRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.sm },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  exportBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, borderRadius: borderRadius.md, backgroundColor: c.primary + '10' },
  exportText: { fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: c.primary },

  searchRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: c.surface, borderWidth: 1, borderColor: c.borderLight, borderRadius: borderRadius.md, paddingHorizontal: spacing.sm, marginBottom: spacing.md },
  searchInput: { flex: 1, fontSize: fontSize.sm, color: c.text, paddingVertical: spacing.sm },

  subCard: { marginBottom: spacing.sm },
  subHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: spacing.xs },
  subName: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  acctBadge: { fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: c.primary, backgroundColor: c.primary + '10', paddingHorizontal: 6, paddingVertical: 1, borderRadius: borderRadius.sm, overflow: 'hidden' },
  statusBadge: { paddingHorizontal: spacing.sm, paddingVertical: 2, borderRadius: borderRadius.full },
  statusText: { fontSize: fontSize.xs, fontWeight: fontWeight.medium },

  usageBarContainer: { height: 6, backgroundColor: c.borderLight, borderRadius: 3, marginBottom: spacing.sm, overflow: 'hidden' },
  usageBarFill: { height: '100%', backgroundColor: c.primary, borderRadius: 3 },

  subStats: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  subStat: { fontSize: fontSize.xs, color: c.textSecondary },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
